import type {ReactElement, RefObject} from 'react';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import type {MediaTypePublishingParams, SocialMediaAccountId} from '@Components/social-media/post.vo';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import type {ContentBackgroundConfig} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import {getPreviewStyleConfig, SocialPostContentType} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {getDefaultDisplayURL, isGoogleBusinessProfileAccount, isPinterestAccount, isYoutubeAccount} from '@Components/social-media/account.vo';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {SocialPostContentMediaWrapper} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-media-wrapper';
import {SocialThumbnail} from '@Components/social-media/components/social-thumbnail';
import type {DesignSelection} from '@Components/social-media/social-media.types';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {SocialPostContentTopPanel} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-top-panel';
import {Text, TextSize} from '@Components/text';
import type {GraphicMeta, GraphicsMetaMapType, PageHashedId, PosterHashedId, SocialMediaPublishingParams} from '@Components/social-media/publish-params.types';
import {SocialPostContentReelTopPanel} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-reel-top-panel/social-post-content-reel-top-panel';
import {SocialPostContentReelBottomPanel} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-reel-bottom-panel';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {noop} from '@Utils/general.util';
import {seekAndPauseAutoplayVideo} from '@Libraries/social-media-grid.library';
import {getAllDesignsFromGraphicsMetaMap, getRenderableDesignFromGraphicsMetaMap} from '@Libraries/social-media-params.library';
import {SocialPostContentStoryTopPanel} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-story-top-panel';
import {SocialPostContentStoryBottomPanel} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-story-bottom-panel';
import {getSocialPlatformString, googleBusinessProfileCtaText, MAX_VISIBLE_ACCOUNT_ICONS, reelOptionsIcons} from './social-post-content-multiposting.types';
import {PreviewVideoPlayer} from '../preview-video-player/preview-video-player';
import styles from './social-post-content-multiposting.module.scss';
import {isIOS} from 'react-device-detect';

export interface SocialPostContentProps {
  type: SocialPostContentType;
  account?: SocialAccount;
  parentContainerRef?: RefObject<HTMLDivElement | null>;
  areDetailsLoading: boolean;
  onLoadCallback?: () => void;
  designsToPublish: DesignSelection[];
  accountsToPublishTo: SocialAccount[];
  publishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>;
  platformMediaType?: PlatformMediaType;
  onOrphanedPreviewContainerClick?: VoidFunction;
  designsGraphicMetaMap: GraphicsMetaMapType;
}

export function SocialPostContent({
  type,
  account,
  parentContainerRef,
  areDetailsLoading,
  onLoadCallback = noop,
  designsToPublish,
  accountsToPublishTo,
  publishingParams,
  onOrphanedPreviewContainerClick = noop,
  platformMediaType = PlatformMediaType.POST,
  designsGraphicMetaMap,
}: SocialPostContentProps): ReactElement<any> {
  type VideoRefType = React.RefObject<HTMLVideoElement | null>;
  const [isMediaLoading, setIsMediaLoading] = useState(true);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const previewWrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef: VideoRefType = useRef(null);
  const [scale, setScale] = useState(0);
  const [graphicItemLoadingMap, setGraphicItemLoadingMap] = useState<Record<PosterHashedId, Record<PageHashedId, boolean>>>({});
  const DEFAULT_WIDTH = 408;
  const maxNumberDisplayablePosters = 3;

  // Keyed on the graphics' content so a status-only post update (e.g. after a successful publish) doesn't reset the loading map for unchanged, already-cached images — they never re-fire onLoad, which would strand the preview on its skeleton.
  const graphicsSignature = useMemo(() => JSON.stringify(designsGraphicMetaMap), [designsGraphicMetaMap]);

  useEffect(() => {
    const initialPosterLoadingMap = getInitialPostersLoadingMap();
    setIsMediaLoading(true);
    setGraphicItemLoadingMap(initialPosterLoadingMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the graphics' content or the account changes, not on every post reference change
  }, [account?.id, platformMediaType, graphicsSignature]);

  const getInitialPostersLoadingMap = (): Record<PosterHashedId, Record<PageHashedId, boolean>> => {
    const initialPosterLoadingMap: Record<PosterHashedId, Record<PageHashedId, boolean>> = {};
    const numberOfAssetsToShow = getNumberOfAssetsToShow();

    let count = 0;

    Object.keys(designsGraphicMetaMap).forEach((posterHashId) => {
      Object.keys(designsGraphicMetaMap[posterHashId]).forEach((pageHashedId) => {
        if (count < numberOfAssetsToShow) {
          initialPosterLoadingMap[posterHashId] ??= {};
          initialPosterLoadingMap[posterHashId][pageHashedId] = false;
          count += 1;
        }
      });
    });

    return initialPosterLoadingMap;
  };

  const getNumberOfAssetsToShow = (): number => {
    if (!doesSomeAccountExist()) {
      return Math.min(getTotalNumberOfPages(), maxNumberDisplayablePosters);
    }

    if (isReelType()) {
      return 1;
    }

    return Math.min(getTotalNumberOfPages(), maxNumberDisplayablePosters);
  };

  useEffect((): void => {
    const initialLoadingMap = getInitialPostersLoadingMap();
    setGraphicItemLoadingMap(initialLoadingMap);
  }, []);

  useEffect((): void => {
    if (!isMediaLoading) {
      onLoadCallback();
    }
  }, [isMediaLoading]);

  const doesSomeAccountExist = (): boolean => {
    return account !== undefined || accountsToPublishTo.length > 0;
  };

  useEffect((): void => {
    if (parentContainerRef?.current) {
      if (doesSomeAccountExist()) {
        const computedScale = getScale(account ?? accountsToPublishTo[0]);
        setScale(computedScale);
        return;
      }

      const computedScale = getDefaultScale();
      setScale(computedScale);
    }
  }, [parentContainerRef?.current, type, account, platformMediaType]);

  const getDefaultScale = (): number => {
    return getOffsetByClientWidth(parentContainerRef?.current?.clientWidth ?? 0) / DEFAULT_WIDTH;
  };

  useEffect((): void => {
    if (contentContainerRef?.current) {
      const height = contentContainerRef.current.clientHeight;
      const width = contentContainerRef.current.clientWidth;

      setContainerDimensions(height, width);

      if (!isMediaLoading || type === SocialPostContentType.MEDIA_RESIZING_FAILURE) {
        contentContainerRef.current.classList.add(styles.loaded);
      } else {
        contentContainerRef.current.classList.remove(styles.loaded);
      }
    }
  }, [contentContainerRef?.current, isMediaLoading, type, publishingParams, scale]);

  const getOffsetByClientWidth = (parentWidth: number): number => {
    if (parentWidth > 600) {
      return parentWidth - 350;
    }

    if (parentWidth > 500) {
      return parentWidth - 260;
    }

    if (parentWidth > 400) {
      return parentWidth - 140;
    }

    if (parentWidth > 330) {
      return parentWidth - 100;
    }

    return parentWidth - 80;
  };

  const getScale = (currentAccount: SocialAccount): number => {
    const config = getPreviewStyleConfig(currentAccount.type, isReelType(), platformMediaType);
    const {width} = config;
    const widthNumeric = parseInt(width, 10);
    const parentWidth = parentContainerRef?.current?.clientWidth || 0;
    return getOffsetByClientWidth(parentWidth) / widthNumeric;
  };

  const setContainerDimensions = (height: number, width: number): void => {
    if (containerRef?.current) {
      containerRef.current.style.height = `${height * scale}px`;
      containerRef.current.style.width = `${width * scale}px`;
    }
  };

  const getTotalNumberOfPages = (): number => {
    return Object.values(designsGraphicMetaMap).reduce((total, pageGraphicsMetaMap) => total + Object.keys(pageGraphicsMetaMap).length, 0);
  };

  const doUpdateMediaLoadingIfNeeded = (updatedPostersLoadingState: Record<PosterHashedId, Record<PageHashedId, boolean>>): void => {
    const allPreviewsLoaded = Object.values(updatedPostersLoadingState).every((pageLoadingState) => {
      return Object.values(pageLoadingState).every((pageLoadingState) => pageLoadingState);
    });

    if (allPreviewsLoaded) {
      setIsMediaLoading(false);
    }
  };

  const onGraphicLoad = ({posterHashedId, pageHashedId}: DesignSelection): void => {
    setGraphicItemLoadingMap((prevState) => {
      const updatedState = {
        ...prevState,
        [posterHashedId]: {
          ...prevState[posterHashedId],
          [pageHashedId]: true,
        },
      };

      doUpdateMediaLoadingIfNeeded(updatedState);
      return updatedState;
    });
  };

  const getOverflowAccountIcon = (hiddenCount: number): ReactElement<any> | null => {
    if (hiddenCount <= 0) {
      return null;
    }

    return (
      <div key="social-post-content-dialog-overflow" className={`${styles.thumbnailContainer} ${styles.overlappingMargin}`}>
        <div className={styles.overflowThumbnail}>
          <Text val={window.i18next.t('pmwjs_plus_count', {number: hiddenCount})} size={TextSize.XSMALL} bold className={styles.overflowCount} />
        </div>
      </div>
    );
  };

  const getLoadingAccountIcons = (): ReactElement<any> | null => {
    if (account) {
      return (
        <div className={styles.thumbnailsContainer}>
          <div key={`social-post-content-dialog-${account.id}`} className={styles.thumbnailContainer}>
            <SocialThumbnail isActive={false} type={account.type} />
          </div>
        </div>
      );
    }

    const visibleAccounts = accountsToPublishTo.slice(0, MAX_VISIBLE_ACCOUNT_ICONS);
    const hiddenAccountCount = accountsToPublishTo.length - visibleAccounts.length;

    return (
      <div className={styles.thumbnailsContainer}>
        {visibleAccounts.map((socialAccount: SocialAccount, index: number): ReactElement<any> => {
          return (
            <div key={`social-post-content-dialog-${socialAccount.id}`} className={`${styles.thumbnailContainer} ${index !== 0 ? styles.overlappingMargin : ''}`}>
              <SocialThumbnail isActive={false} type={socialAccount.type} />
            </div>
          );
        })}
        {getOverflowAccountIcon(hiddenAccountCount)}
      </div>
    );
  };

  const getClassesForYoutubeContainer = (currentAccount: SocialAccount): string => {
    if (isYoutubeAccount(currentAccount)) {
      return styles.contentContainerYoutube;
    }

    return '';
  };

  const getEffectiveDesigns = (): DesignSelection[] => {
    if (designsToPublish.length > 0) {
      return designsToPublish;
    }

    return getAllDesignsFromGraphicsMetaMap(designsGraphicMetaMap);
  };

  const orphanedPreview = (): ReactElement<any> | null => {
    const shouldShowAccountIcons = type === SocialPostContentType.DEFAULT || areDetailsLoading;
    const config = {width: '408px', height: '500px', scale: `${scale}`};

    return (
      <div ref={contentContainerRef} style={config} className={styles.contentBackground}>
        <div className={`${styles.loadingContainer} _full-width`}>
          <div className={`${styles.firstRow} _full-width`}>
            <SkeletonBrick isPulsating={isMediaLoading} className={`radius-round spacing-m-r-4 ${styles.profilePicLoader}`} width={40} height={40} />
            <SkeletonBrick isPulsating={isMediaLoading} className="radius-round spacing-m-r-4" width={207} height={23} />
            {shouldShowAccountIcons ? getLoadingAccountIcons() : null}
          </div>
          <SkeletonBrick isPulsating={isMediaLoading} className="radius-round spacing-m-b-3" height={16} />
          <SkeletonBrick isPulsating={isMediaLoading} className="radius-round  spacing-m-b-3 spacing-m-r-3" width={184} height={16} />
          <SocialPostContentMediaWrapper
            graphicsLoadingMap={graphicItemLoadingMap}
            designsGraphicsMetaMap={designsGraphicMetaMap}
            designs={getEffectiveDesigns()}
            onAssetLoad={onGraphicLoad}
            onOrphanedPreviewContainerClick={onOrphanedPreviewContainerClick}
            doRetryOnError
          />
        </div>
      </div>
    );
  };

  const isReelType = (): boolean => {
    return platformMediaType === PlatformMediaType.REELS;
  };

  const getCaptionFromParams = (activePublishingParams: SocialMediaPublishingParams): string => {
    if ('caption' in activePublishingParams && activePublishingParams.caption !== '') {
      return activePublishingParams.caption;
    }

    return '';
  };

  const getGoogleBusinessProfileCtaText = (): string | null => {
    if (!account) {
      return null;
    }

    const publishingParamsForMediaType = publishingParams[account.id][platformMediaType];

    if (!publishingParamsForMediaType) {
      return null;
    }

    if ('ctaType' in publishingParamsForMediaType && publishingParamsForMediaType.ctaType !== null) {
      return googleBusinessProfileCtaText[publishingParamsForMediaType.ctaType];
    }

    return null;
  };

  const getCaptionHolderForInstagram = (): ReactElement<any> | null => {
    if (!account || account.type !== SocialPlatform.INSTAGRAM || Object.keys(publishingParams).length === 0 || !publishingParams[account.id][platformMediaType]) {
      return null;
    }

    const textClasses = `color-neutral-28 ${styles.overflowingCaptions}`;
    const postPublishingParams = publishingParams[account.id][platformMediaType];

    if (!postPublishingParams) {
      return null;
    }

    return (
      <div className={`${styles.captionHolder} _full-width _fit-height flex-row-align-center ${getTotalNumberOfPages() === 1 ? 'spacing-m-t-3' : ''}`}>
        <Text val={account?.name || ''} className={`content-body spacing-m-r-2 ${styles.accountNameHolder}`} size={TextSize.XSMALL} bold />
        <Text val={getCaptionFromParams(postPublishingParams)} className={textClasses} size={TextSize.XSMALL} />
      </div>
    );
  };

  const getCaptionHolderForGoogleBusinessProfile = (): ReactElement<any> | null => {
    if (!account || !isGoogleBusinessProfileAccount(account)) {
      return null;
    }

    let textClasses = styles.overflowingCaptions;

    if (!publishingParams[account.id][platformMediaType]) {
      return null;
    }

    const publishingParamsForMediaType = publishingParams[account.id][platformMediaType];
    let caption = publishingParamsForMediaType ? getCaptionFromParams(publishingParamsForMediaType) : '';

    if (caption === '') {
      caption = window.i18next.t('pmwjs_post_update_for_x', {accountName: account.name});
      textClasses += ' content-disabled';
    } else {
      textClasses += ' color-neutral-28';
    }

    return (
      <div className={`${styles.captionHolder} _full-width _fit-height flex-row-align-center spacing-p-3 spacing-b-1`}>
        <Text val={caption} className={textClasses} size={TextSize.XSMALL} />
      </div>
    );
  };

  const renderGoogleBusinessProfileCTA = (): ReactElement<any> | null => {
    if (!isGoogleBusinessProfileAccount(account)) {
      return null;
    }

    const ctaText = getGoogleBusinessProfileCtaText();
    if (!ctaText) {
      return null;
    }

    const textClasses = `content-primary ${styles.overflowingCaptions}`;
    return (
      <div className={`${styles.captionHolder} _full-width _fit-height flex-row-align-center spacing-m-t-3`}>
        <Text val={ctaText} className={textClasses} size={TextSize.XSMALL} />
      </div>
    );
  };

  const getPinterestFooter = (): ReactElement<any> | null => {
    if (!isPinterestAccount(account)) {
      return null;
    }

    return (
      <div className={styles.pinterestFooterContainer}>
        <div className="flex-row-justify-between">
          <div className="flexbox">
            <div className="spacing-p-t-2 spacing-p-b-0 spacing-p-l-2 spacing-p-r-2 spacing-m-r-2">
              <Icon size={IconSize.SIZE_ICON_20} icon="icon-message" />
            </div>
            <div className="spacing-p-t-2 spacing-p-b-0 spacing-p-l-2 spacing-p-r-2">
              <Icon size={IconSize.SIZE_ICON_20} icon="icon-upload" />
            </div>
          </div>
          <div>
            <div className="spacing-p-t-2 spacing-p-b-0 spacing-p-l-2 spacing-p-r-2">
              <Icon size={IconSize.SIZE_ICON_20} icon="icon-pin" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getReelWrapperStylesForYouTube = (currentAccount: SocialAccount): string => {
    if (isYoutubeAccount(currentAccount)) {
      return styles.youtubeReel;
    }

    return '_full-width';
  };

  const getDesignForReel = (): DesignSelection | null => {
    return getRenderableDesignFromGraphicsMetaMap(designsToPublish, designsGraphicMetaMap);
  };

  const getReelGraphicMeta = (design: DesignSelection): GraphicMeta | undefined => {
    return designsGraphicMetaMap[design.posterHashedId]?.[design.pageHashedId];
  };

  const getCoverUrlForReels = (design: DesignSelection): string | undefined => {
    return getReelGraphicMeta(design)?.cover?.coverUrl;
  };

  const doesVideoNeedSeekingForIOSPreview = (design: DesignSelection): boolean => {
    return isIOS && !getCoverUrlForReels(design);
  };

  const getVideoForRenderingReels = (design: DesignSelection, graphicUrl: string): ReactElement<any> => {
    if (doesVideoNeedSeekingForIOSPreview(design)) {
      const FIRST_FRAME_SEC = 0.1;

      return (
        <video
          onLoadedData={(e) => {
            onGraphicLoad(design);
            seekAndPauseAutoplayVideo(e, FIRST_FRAME_SEC);
          }}
          ref={videoRef}
          className={`${styles.postStylesVideo} ${styles.reelStyles}`}
          loop
          src={graphicUrl}
          muted
          autoPlay
          playsInline
        />
      );
    }

    return (
      <video
        onLoadedMetadata={() => {
          onGraphicLoad(design);
        }}
        ref={videoRef}
        className={`${styles.postStylesVideo} ${styles.reelStyles}`}
        loop
        src={graphicUrl}
        muted
        poster={getCoverUrlForReels(design)}
        playsInline
      />
    );
  };

  const renderReels = (currentAccount: SocialAccount): ReactElement<any> | null => {
    const design = getDesignForReel();
    const graphicMeta = design ? getReelGraphicMeta(design) : undefined;

    if (!design || !graphicMeta) {
      return orphanedPreview();
    }

    const configStyles = getContentBackgroundDimensions(currentAccount);

    return (
      <div ref={contentContainerRef} style={configStyles} className={`${styles.contentBackground} ${getClassesForYoutubeContainer(currentAccount)}`}>
        <div className={`${styles.reelWrapper} ${getReelWrapperStylesForYouTube(currentAccount)}`}>
          <SocialPostContentReelTopPanel account={currentAccount} />
          {getVideoForRenderingReels(design, graphicMeta.graphicURL)}
          <PreviewVideoPlayer isReel={isReelType()} videoRef={videoRef} />
          <SocialPostContentReelBottomPanel platformMediaType={platformMediaType} postPublishingParams={publishingParams} account={currentAccount} />
        </div>
        {getSideOptionsYoutube()}
      </div>
    );
  };

  const renderGoogleBusinessProfilePreview = (): ReactElement<any> | null => {
    if (!account) {
      return null;
    }

    const dimensionsConfig = getContentBackgroundDimensions(account);

    return (
      <div ref={contentContainerRef} style={dimensionsConfig} className={`${styles.contentBackground} spacing-p-0`}>
        <div className={`${styles.loadingContainer} _full-width`}>
          <SocialPostContentTopPanel platformMediaType={platformMediaType} publishingParams={publishingParams} account={account} />
          {getMediaInformation()}
          {getCaptionHolderForGoogleBusinessProfile()}
          {renderGoogleBusinessProfileCTA()}
        </div>
      </div>
    );
  };

  const getContentBackgroundDimensions = (currentAccount: SocialAccount): ContentBackgroundConfig => {
    const configDimensions = getPreviewStyleConfig(currentAccount.type, isReelType(), platformMediaType);
    return {...configDimensions, scale: `${scale}`};
  };

  const getMediaInformation = (): ReactElement<any> => {
    if (type === SocialPostContentType.MEDIA_RESIZING_FAILURE) {
      return (
        <div className={styles.resizeFailureContainer}>
          <Icon
            flatIconType={{color: FlatIconColor.DANGER, type: FlatIconColorType.DARK}}
            type={IconType.FLAT}
            shape={IconShape.CIRCLE}
            size={IconSize.SIZE_ICON_20}
            className={`${styles.errorIcon} spacing-m-b-2`}
            icon="icon-exclamation-triangle"
          />
          <Text className={`content-danger ${styles.errorText}`} val={window.i18next.t('pmwjs_resizing_design_error')} />
        </div>
      );
    }

    return (
      <SocialPostContentMediaWrapper
        graphicsLoadingMap={graphicItemLoadingMap}
        account={account}
        designsGraphicsMetaMap={designsGraphicMetaMap}
        designs={getEffectiveDesigns()}
        onAssetLoad={onGraphicLoad}
        doRetryOnError
      />
    );
  };

  const renderStories = (currentAccount: SocialAccount): ReactElement<any> => {
    const configStyles = getContentBackgroundDimensions(currentAccount);

    return (
      <div ref={contentContainerRef} className={styles.contentBackground} style={configStyles}>
        <div className={styles.storyParent}>
          <SocialPostContentStoryTopPanel account={currentAccount} />
          <SocialPostContentMediaWrapper
            designsGraphicsMetaMap={designsGraphicMetaMap}
            graphicsLoadingMap={graphicItemLoadingMap}
            designs={getEffectiveDesigns()}
            onAssetLoad={onGraphicLoad}
            doRetryOnError
            enableVideoAutoplay
          />
          <SocialPostContentStoryBottomPanel account={currentAccount} />
        </div>
      </div>
    );
  };

  const getAccountPreview = (): ReactElement<any> | null => {
    if (!account || areDetailsLoading) {
      return orphanedPreview();
    }

    if ((isReelType() || isYoutubeAccount(account)) && type !== SocialPostContentType.MEDIA_RESIZING_FAILURE) {
      return renderReels(account);
    }

    if (isGoogleBusinessProfileAccount(account)) {
      return renderGoogleBusinessProfilePreview();
    }

    if (platformMediaType === PlatformMediaType.STORIES && type !== SocialPostContentType.MEDIA_RESIZING_FAILURE) {
      return renderStories(account);
    }

    const dimensionsConfig = getContentBackgroundDimensions(account);

    return (
      <div ref={contentContainerRef} style={dimensionsConfig} className={styles.contentBackground} key={account.id}>
        <div className={`${styles.loadingContainer} _full-width`}>
          <SocialPostContentTopPanel platformMediaType={platformMediaType} publishingParams={publishingParams} account={account} />
          {getMediaInformation()}
          {getPinterestFooter()}
          {getCaptionHolderForInstagram()}
        </div>
      </div>
    );
  };

  const getPreview = (): ReactElement<any> | null => {
    if (accountsToPublishTo.length === 0 && !account) {
      return orphanedPreview();
    }

    switch (type) {
      case SocialPostContentType.MEDIA_RESIZING:
        return null;
      case SocialPostContentType.MEDIA_RESIZING_FAILURE:
      case SocialPostContentType.ACCOUNT_PREVIEW:
        return getAccountPreview();
      case SocialPostContentType.DEFAULT:
      case SocialPostContentType.ORPHANED_PREVIEW:
      default:
        return orphanedPreview();
    }
  };

  const renderReelOptionsIcons = (): ReactElement<any>[] => {
    const optionIcons = reelOptionsIcons[getSocialPlatformString(account)];
    const iconContainer = [];
    for (let i = 0; i < optionIcons.length; i++) {
      iconContainer.push(<Icon key={i} className="spacing-m-t-2" type={IconType.FLAT} icon={optionIcons[i]} shape={IconShape.CIRCLE} size={IconSize.SIZE_ICON_16} />);
    }

    return iconContainer;
  };

  const getSideOptionsYoutube = (): ReactElement<any> | null => {
    if (isYoutubeAccount(account)) {
      return (
        <div className={styles.youtubeSideOptions}>
          <div className={styles.instagramReelProfileInfo}>
            {renderReelOptionsIcons()}
            <img
              alt={window.i18next.t('pmwjs_account_display_alt')}
              className={`spacing-m-t-2 ${styles.youtubeProfileImage}`}
              src={account?.displayURL ?? getDefaultDisplayURL()}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="_full-height">
      <CircularProgressLoader classes={isMediaLoading && type !== SocialPostContentType.MEDIA_RESIZING_FAILURE ? styles.loaderClass : '_hidden'} size={LOADER_SIZE.MEDIUM} />
      <div ref={previewWrapperRef} className={styles.previewWrapper}>
        <div ref={containerRef} className={styles.previewContent}>
          {getPreview()}
        </div>
      </div>
    </div>
  );
}
